// WS5 PR5 — Ride-mode state primitives (PR5 ride-mode + pin).
//
// Razionale: il GS ONIRI e' anisotropo per qualita' (pipeline Maxscript +
// Seurat capture-headbox: 4096px center + 2048px sides). DENTRO AABB headbox
// → qualita' massima. FUORI AABB → qualita' in calo (splat interpolati da
// viewpoint non destinati). Il pattern attuale teleport flyToCamera 800ms
// espone l'utente a zone non calibrate durante il glide. Ride-mode (player
// → headbox.center target con linear path + eased velocity + look-guided)
// maschera il limite tecnico → "stanze esplorabili + corridoi cinematici".
//
// Layer puro: tipi + costanti. Reducer in state-machine.ts, pure math in
// motion.ts / edge-trigger.ts / pin-sprite.ts. Orchestrator wiring in
// src/orchestrator/orchestrator.ts.
//
// Pattern speculare di walk/* e viewbox/* (PR3.x): single-source-of-truth
// nello slice owned dall'orchestrator, snapshot piatto per UI/HUD, no-op
// transitions ritornano stesso reference per dispatch short-circuit.

import type { Vec3 } from '../api/types.js';

// ============================================================================
// Constants
// ============================================================================

/** Velocita' di crociera durante ride (m/s). Tuning 2026-06-06: 5 m/s per
 *  matchare Unity Oniri_VShop SC_MoveTo (5 m/s linear). Roma 4vb stanze
 *  adiacenti ~4m → durata ride ~800ms ("scatto deciso" tipo Unity). */
export const RIDE_CRUISE_SPEED_MPS = 5.0;

/** Clamp durata ride. distanza/speed clampato in [min, max].
 *  Min 350ms: distanze < 1.75m ricalcolano short ride (evita "rimbalzo").
 *  Max 900ms: distanze > 4.5m non superano 900ms a 5 m/s. */
export const RIDE_MIN_DURATION_MS = 350;
export const RIDE_MAX_DURATION_MS = 900;

/** 2-phase asymmetric curve (Unity-like snappy + soft landing).
 *  Tuning 2026-06-06: accel=0 (full speed istantaneo) + decel=0.1 (10% decel
 *  ramp finale per evitare stop secco). Feel: "scatto Unity" senza jolt al
 *  fermo. easedT gestisce accelEnd=0 con guard branch. */
export const RIDE_ACCEL_FRACTION = 0;
export const RIDE_DECEL_FRACTION = 0.1;

/** Cooldown dopo COMPLETE durante il quale walk-to-edge auto trigger NON
 *  spara nuovi ride. Evita re-trigger immediato se l'utente continua W
 *  appena spawnato nella nuova viewbox. */
export const RIDE_COOLDOWN_POST_MS = 1500;

/** Edge trigger thresholds (walk-to-edge auto). Conservativi per evitare
 *  false positive durante normale walk dentro AABB. */
export const EDGE_VELOCITY_MIN_MPS = 0.5; // walking speed >0.5 m/s
export const EDGE_OUTWARD_COSINE_MIN = 0.6; // ~53° max angolo vel vs radial outward
export const EDGE_NEAR_FACE_M = 0.5; // entro 50cm da AABB face
export const EDGE_PERSISTENCE_MS = 180; // 3 frame @60fps consecutivi
export const EDGE_CANDIDATE_MAX_M = 12.0; // viewbox oltre 12m → skip
export const EDGE_CANDIDATE_COSINE_MIN = 0.55; // angolo cono ~57° vel vs toVb

/** Pin visibility thresholds (Sprite worldspace). */
export const PIN_NEAR_DIM_M = 1.5; // < 1.5m → dim + non-clickable (anti-misclick)
export const PIN_FADE_NEAR_M = 3.0; // fade range start
export const PIN_FADE_FAR_M = 25.0; // fade range end
export const PIN_OPACITY_MAX = 1.0;
export const PIN_OPACITY_MIN = 0.35;
export const PIN_OPACITY_DIM = 0.15;
export const PIN_OFFSET_Y_M = 0.3; // headbox.center.y + floor+eye+0.3 → ~30cm sopra occhi

/** Occlusion check throttle (Hz). 10Hz = 100ms tick. Costo: ~N pin
 *  raycast a 10Hz su SplatMesh, accettabile fino a N=8 pin. */
export const PIN_OCCLUSION_CHECK_INTERVAL_MS = 100;

/** EPS contro splat layer pavimento immediato. Se hit.distance < pin.distance
 *  - EPS → considerato occluso. EPS = 0.3m: a 1.5m+ dal pin, splat del pavimento
 *  immediato (5cm sotto camera) NON contano come occlusione del pin. */
export const PIN_OCCLUSION_EPS_M = 0.3;

/** Mobile pin cap. Top-3 per screen-distance da centro viewport (ranking che
 *  privilegia pin "guardati" dall'utente). */
export const PIN_MOBILE_TOP_K = 3;

// ============================================================================
// Ride phase + state
// ============================================================================

/** Fase corrente della ride state machine. */
export type RidePhase = 'idle' | 'ride_to_viewbox' | 'ride_to_hotspot';

/** Trigger che ha originato il ride (telemetria + edge-cooldown gate). */
export type RideTrigger = 'pin' | 'edge_auto' | 'hotspot_click';

/** Slice di stato dedicato a ride. Owned dall'orchestrator. Mutex con
 *  viewboxState.teleportPhase, walkState.active+input, hotspots open,
 *  cinematic active, XR starting/in-session (vedi orchestrator). */
export interface RideState {
  phase: RidePhase;
  /** Posizione camera al momento di START (Vec3 world). */
  startPos: Vec3;
  /** Yaw camera al momento di START (rad). */
  startYaw: number;
  /** Pitch camera al momento di START (rad). */
  startPitch: number;
  /** Posizione target finale (headbox.center per ride_to_viewbox,
   *  hotspot.pos per ride_to_hotspot). */
  targetPos: Vec3;
  /** Anchor lookAt (centroide stanza per viewbox-ride, hotspot per
   *  hotspot-ride). Usato per look-guided blend yaw/pitch. */
  targetAnchor: Vec3;
  /** Yaw finale (rad), calcolato all'START per evitare ricomputo per tick. */
  targetYaw: number;
  /** Pitch finale (rad). */
  targetPitch: number;
  /** Durata totale ride (ms), clampata in [RIDE_MIN_DURATION_MS, RIDE_MAX_DURATION_MS]. */
  durationMs: number;
  /** Timestamp performance.now() al momento di START. */
  startTimeMs: number;
  /** Array idx viewbox target (ride_to_viewbox), -1 per hotspot. */
  targetVbArrayIdx: number;
  /** Cooldown end time (ms perf.now). Durante ride: invariato.
   *  Dopo COMPLETE: now+RIDE_COOLDOWN_POST_MS. */
  cooldownUntilMs: number;
  /** Trigger originario (telemetria + gating). */
  trigger: RideTrigger;
}

/** Snapshot piatto per UI / event emitter. */
export interface RideSnapshot {
  phase: RidePhase;
  durationMs: number;
  elapsedMs: number;
  trigger: RideTrigger;
  targetVbArrayIdx: number;
}

/** Eventi reducer. */
export type RideEvent =
  | {
      type: 'START_TO_VIEWBOX';
      arrayIdx: number;
      startPos: Vec3;
      startYaw: number;
      startPitch: number;
      targetPos: Vec3;
      targetAnchor: Vec3;
      targetYaw: number;
      targetPitch: number;
      durationMs: number;
      startTimeMs: number;
      trigger: RideTrigger;
    }
  | {
      type: 'START_TO_HOTSPOT';
      hotspotIdx: number;
      startPos: Vec3;
      startYaw: number;
      startPitch: number;
      targetPos: Vec3;
      targetAnchor: Vec3;
      targetYaw: number;
      targetPitch: number;
      durationMs: number;
      startTimeMs: number;
    }
  | { type: 'COMPLETE'; nowMs: number }
  | { type: 'ABORT'; nowMs: number }
  | { type: 'RESET' };

/** Stato iniziale. */
export const INITIAL_RIDE_STATE: RideState = {
  phase: 'idle',
  startPos: [0, 0, 0],
  startYaw: 0,
  startPitch: 0,
  targetPos: [0, 0, 0],
  targetAnchor: [0, 0, 0],
  targetYaw: 0,
  targetPitch: 0,
  durationMs: 0,
  startTimeMs: 0,
  targetVbArrayIdx: -1,
  cooldownUntilMs: 0,
  trigger: 'pin',
};

/** Re-export Vec3 per chiarezza modulo. */
export type { Vec3 };
